import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect } from 'react'

import { AddNewProgramFormModel } from '../../AddNewProgram.types'

import { PartnerFieldOptionsProps } from './PartnerFieldsOptions.types'
import { Flex, Icon, TextInput, FormField } from '@percent/lemonade'
import styles from './PartnerFieldOptions.module.scss'

const MAX_SELECT_OPTIONS_LENGTH = 15
const MAX_MULTI_SELECT_OPTIONS_LENGTH = 5

export function PartnerFieldOptions({ partnerFieldIndex }: PartnerFieldOptionsProps) {
  const { t } = useTranslation()
  const {
    control,
    register,
    trigger,
    formState: { errors }
  } = useFormContext<AddNewProgramFormModel>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: `partnerFields.${partnerFieldIndex}.options`
  })

  const partnerFields = useWatch({ control, name: 'partnerFields' })

  useEffect(() => {
    trigger()
  }, [partnerFields, trigger])

  const canAddNewOption = useCallback(
    (index: number) => {
      const multiSelectCondition =
        partnerFields?.[partnerFieldIndex].type === 'multiple_choice' &&
        fields.length - 1 === index &&
        fields.length < MAX_MULTI_SELECT_OPTIONS_LENGTH

      const selectCondition =
        partnerFields?.[partnerFieldIndex].type === 'select' &&
        fields.length - 1 === index &&
        fields.length < MAX_SELECT_OPTIONS_LENGTH

      return multiSelectCondition || selectCondition
    },
    [fields.length, partnerFieldIndex, partnerFields]
  )

  return (
    <Flex direction="column" gap={8}>
      {fields.map((field, index) => (
        <Flex direction="row" gap={16} align="center" key={field.id}>
          <FormField
            label={t('form.key')}
            necessity="required"
            statusMessage={errors.partnerFields?.[partnerFieldIndex]?.options?.[index]?.key?.message}
          >
            <TextInput
              {...register(`partnerFields.${partnerFieldIndex}.options.${index}.key`)}
              placeholder={t('form.keyPlaceholder')}
            />
          </FormField>
          <FormField
            label={t('form.value')}
            necessity="required"
            statusMessage={errors.partnerFields?.[partnerFieldIndex]?.options?.[index]?.value?.message}
          >
            <TextInput
              {...register(`partnerFields.${partnerFieldIndex}.options.${index}.value`)}
              placeholder={t('form.valuePlaceholder')}
            />
          </FormField>
          <Flex direction="row" gap={8} className={styles.actionBtnWrapper}>
            {fields.length > 1 && (
              <button
                type="button"
                data-testid="remove-option-button"
                onClick={() => remove(index)}
                className={styles.actionBtn}
              >
                <Icon name="bin" color="alert400" size={5} />
              </button>
            )}
            {canAddNewOption(index) && (
              <button
                type="button"
                data-testid="add-option-button"
                onClick={() => append({ key: '', value: '' })}
                className={styles.actionBtn}
              >
                <Icon name="add" color="success400" size={5} />
              </button>
            )}
          </Flex>
        </Flex>
      ))}
    </Flex>
  )
}
